import React, { useState, useEffect, useCallback, useRef, memo } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import {
  DuelTab,
  DuelTabActive,
  NewsTab,
  NewsTabActive,
  ChatTab,
  ChatTabActive,
  SearchTab,
  SearchTabActive,
  PracticeTab,
  PracticeTabActive,
} from '../../assets/images';
import { MixpanelEvent, sendPostHogEvent } from '../../utils/mixpanelUtil';
import { useTheme } from '../../context/themeProvider';
import './styles.css';
import { useLocalStorageStore } from '../../stores/localStorageStore';

const navItems = [
  { to: '/duel', icon: DuelTab, activeIcon: DuelTabActive, label: 'Duel' },
  { to: '/news', icon: NewsTab, activeIcon: NewsTabActive, label: 'News' },
  { to: '/chat', icon: ChatTab, activeIcon: ChatTabActive, label: 'Chat' },
  { to: '/pyq', icon: SearchTab, activeIcon: SearchTabActive, label: 'PYQs' },
  {
    to: '/revision',
    icon: PracticeTab,
    activeIcon: PracticeTabActive,
    label: 'Revision',
  },
];

const NavItem = memo(
  ({
    to,
    icon,
    activeIcon,
    label,
    isActive,
    handleClick,
    colorScheme,
    linkStyle,
  }) => (
    <NavLink
      to={to}
      style={{ ...linkStyle, color: linkStyle.color(to) }}
      className={
        isActive(to)
          ? colorScheme === 'dark'
            ? 'dark-theme-active'
            : 'light-theme-active'
          : ''
      }
      onClick={(e) => handleClick(e, to)}
    >
      <img
        src={isActive(to) ? activeIcon : icon}
        alt={`${label} icon`}
        width='24'
        height='24'
        loading='lazy'
      />
      <div className='label font-poppins text-xs'>{label}</div>
    </NavLink>
  )
);

const Navbar = () => {
  const location = useLocation();
  const { colorScheme } = useTheme();
  const navigate = useNavigate();

  const [currentTab, setCurrentTab] = useState(location.pathname);
  const [pageEntryTime, setPageEntryTime] = useState(Date.now());

  const isNewUser = useLocalStorageStore.getState().newUser === 'true';

  useEffect(() => {
    setCurrentTab(location.pathname);
    setPageEntryTime(Date.now());
  }, [location.pathname]);

  const handleClick = useCallback(
    (e, to) => {
      e.preventDefault();

      const now = Date.now();
      const timeDiff = now - pageEntryTime;

      if (isNewUser) {
        if (timeDiff >= 15000) {
          navigate(to);
          setCurrentTab(to);
          setPageEntryTime(now);
        } else {
          navigate(to);
          setCurrentTab(to);
          setPageEntryTime(now);
        }
      } else {
        navigate(to);
        setCurrentTab(to);
        setPageEntryTime(now);
      }
      MixpanelEvent('footer_selection', {
        tab: to.slice(1),
        default_selection: to.slice(1) === 'news',
      });
      sendPostHogEvent('footer_selection', {
        tab: to.slice(1),
        default_selection: to.slice(1) === 'news',
      });
    },
    [navigate, currentTab, pageEntryTime, isNewUser]
  );

  const isActive = useCallback(
    (path) => {
      return (
        location.pathname === path ||
        (path === '/pyq' && location.pathname.startsWith('/pyq'))
      );
    },
    [location.pathname]
  );

  const linkStyle = {
    textDecoration: 'none',
    color: (path) =>
      isActive(path)
        ? colorScheme === 'dark'
          ? '#ffffff'
          : '#000000'
        : colorScheme === 'dark'
          ? '#8B8B8B'
          : '#747474',
    textAlign: 'center',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '10px 0',
  };

  const boxClass = `navbar-container flex justify-around py-2.5 bg-background border-t ${
    colorScheme === 'dark' ? 'border-[#2E2E2E]' : ''
  } absolute bottom-0 left-0 right-0 w-full z-[999] shadow-inner`;

  return (
    <div className={boxClass}>
      {navItems.map((item) => (
        <NavItem
          key={item.to}
          {...item}
          isActive={isActive}
          handleClick={handleClick}
          colorScheme={colorScheme}
          linkStyle={linkStyle}
        />
      ))}
    </div>
  );
};

export default memo(Navbar);
